import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Download, Hand, ImagePlus, MousePointer2, Redo2, RotateCcw, Undo2, Upload, X } from 'lucide-react';
import clsx from 'clsx';

import { TOOLS } from '../../config';
import { useMediaStudio } from '../../context/MediaStudioContext';

import scss from './MediaStudioTopBar.module.scss';

const TOOL_META = {
  [TOOLS.SELECT]: { icon: MousePointer2, labelKey: 'selectTool' },
  [TOOLS.HAND]: { icon: Hand, labelKey: 'handTool' },
};

const pickFile = (onFile) => (event) => {
  const file = event.target.files?.[0];
  if (file) {
    onFile(file);
  }
  event.target.value = '';
};

function MediaStudioTopBar() {
  const { t } = useTranslation('mediaStudio');
  const { config, features, editor, files, notice, onClose } = useMediaStudio();
  const openInputRef = useRef(null);
  const layerInputRef = useRef(null);
  const { tool } = editor;
  const visibleTools = config.tools.filter((id) => TOOL_META[id]);

  return (
    <header className={scss.bar}>
      <div className={scss.group}>
        {features.openImage ? (
          <button
            type='button'
            className={scss.button}
            onClick={() => openInputRef.current?.click()}
            title={t('openImage')}
            aria-label={t('openImage')}>
            <Upload size={16} />
            <span className={scss.label}>{t('openImage')}</span>
          </button>
        ) : null}
        {features.addLayer ? (
          <button
            type='button'
            className={scss.button}
            onClick={() => layerInputRef.current?.click()}
            title={t('addLayer')}
            aria-label={t('addLayer')}>
            <ImagePlus size={16} />
            <span className={scss.label}>{t('addLayer')}</span>
          </button>
        ) : null}
        {features.openImage ? (
          <input ref={openInputRef} type='file' accept='image/*' hidden onChange={pickFile(files.openImage)} />
        ) : null}
        {features.addLayer ? (
          <input ref={layerInputRef} type='file' accept='image/*' hidden onChange={pickFile(files.addLayer)} />
        ) : null}
      </div>

      {features.undoRedo ? (
        <div className={scss.group}>
          <button
            type='button'
            className={scss.button}
            onClick={editor.undo}
            disabled={!editor.canUndo}
            title={t('undo')}
            aria-label={t('undo')}>
            <Undo2 size={16} />
            <span className={scss.label}>{t('undo')}</span>
          </button>
          <button
            type='button'
            className={scss.button}
            onClick={editor.redo}
            disabled={!editor.canRedo}
            title={t('redo')}
            aria-label={t('redo')}>
            <Redo2 size={16} />
            <span className={scss.label}>{t('redo')}</span>
          </button>
        </div>
      ) : null}

      {visibleTools.length ? (
        <div className={clsx(scss.group, scss.toolGroup)} role='toolbar' aria-label={t('selectTool')}>
          {visibleTools.map((id) => {
            const { icon: Icon, labelKey } = TOOL_META[id];
            return (
              <button
                key={id}
                type='button'
                className={clsx(scss.toolButton, tool === id && scss.toolButtonActive)}
                onClick={() => editor.setTool(id)}
                title={t(labelKey)}
                aria-label={t(labelKey)}
                aria-pressed={tool === id}>
                <Icon size={16} strokeWidth={2} />
                <span className={scss.label}>{t(labelKey)}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {tool === TOOLS.CROP ? (
        <div className={clsx(scss.group, scss.cropActions)}>
          <button
            type='button'
            className={clsx(scss.button, scss.primary)}
            onClick={() => notice.run(editor.applyCrop, 'actionFailed')}
            disabled={editor.isApplyingCrop}
            title={t('applyCrop')}
            aria-label={t('applyCrop')}>
            <Check size={16} />
            <span className={scss.label}>{t('applyCrop')}</span>
          </button>
          <button
            type='button'
            className={scss.button}
            onClick={() => editor.setTool(TOOLS.SELECT)}
            title={t('cancelCrop')}
            aria-label={t('cancelCrop')}>
            <X size={16} />
            <span className={scss.label}>{t('cancelCrop')}</span>
          </button>
        </div>
      ) : null}

      <div className={scss.spacer} />

      <div className={scss.group}>
        {features.download ? (
          <button
            type='button'
            className={clsx(scss.button, scss.primary)}
            onClick={files.download}
            disabled={files.isSaving}
            title={t('download')}
            aria-label={t('download')}>
            <Download size={16} />
            <span className={scss.label}>{t('download')}</span>
          </button>
        ) : null}

        {files.saveActions.map((action) => (
          <button
            key={action.id}
            type='button'
            className={scss.button}
            onClick={() => files.save(action)}
            disabled={files.isSaving || action.disabled}>
            {action.label}
          </button>
        ))}

        {features.reset ? (
          <button
            type='button'
            className={scss.button}
            onClick={files.requestReset}
            title={t('reset')}
            aria-label={t('reset')}>
            <RotateCcw size={16} />
            <span className={scss.label}>{t('reset')}</span>
          </button>
        ) : null}

        {onClose ? (
          <button type='button' className={scss.button} onClick={onClose} title={t('close')} aria-label={t('close')}>
            <X size={16} />
            <span className={scss.label}>{t('close')}</span>
          </button>
        ) : null}
      </div>
    </header>
  );
}

export default MediaStudioTopBar;
